import React, { useState } from 'react';
import { useLms } from '../../context/LmsContext';
import { Card, DataTable, FormRow, PageHeader, StatusBadge } from '../../components/ui';
import { normalizeUserRoles } from '../../utils/navigation';
import { fullName } from '../../utils/lmsSelectors';

export default function AdminGroups() {
  const { data, currentUser, createGroup } = useLms();
  const learners = data.users.filter((user) => normalizeUserRoles(user).includes('learner'));
  const [form, setForm] = useState({ name: '', description: '', memberIds: [] });
  const [message, setMessage] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const filteredLearners = learners.filter((learner) => `${fullName(learner)} ${learner.email} ${learner.team || ''}`.toLowerCase().includes(memberSearch.trim().toLowerCase()));

  const submit = (event) => {
    event.preventDefault();
    const result = createGroup(form, currentUser.id);
    setMessage(result.ok ? 'Group created.' : result.message);
    if (result.ok) setForm({ name: '', description: '', memberIds: [] });
  };

  const toggleMember = (id) => {
    setForm((current) => ({
      ...current,
      memberIds: current.memberIds.includes(id) ? current.memberIds.filter((memberId) => memberId !== id) : [...current.memberIds, id]
    }));
  };

  const toggleVisibleMembers = () => {
    setForm((current) => {
      const visibleIds = filteredLearners.map((learner) => learner.id);
      const allSelected = visibleIds.length > 0 && visibleIds.every((id) => current.memberIds.includes(id));
      return {
        ...current,
        memberIds: allSelected ? current.memberIds.filter((id) => !visibleIds.includes(id)) : [...new Set([...current.memberIds, ...visibleIds])]
      };
    });
  };

  const rows = data.groups.map((group) => ({
    ...group,
    members: group.memberIds.map((id) => fullName(data.users.find((user) => user.id === id))).join(', ')
  }));

  return (
    <>
      <PageHeader title="Groups and teams" eyebrow="Admin" description="Use groups for bulk training assignment and training matrix reporting." />
      <div className="grid two uneven">
        <Card>
          <h2>Create group</h2>
          <form className="form-stack" onSubmit={submit}>
            <FormRow label="Group name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormRow>
            <FormRow label="Description"><textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></FormRow>
            <div>
              <strong>Select members</strong>
              <div className="toolbar between">
                <input className="search-input" placeholder="Search learner by name, email, or team" value={memberSearch} onChange={(e) => setMemberSearch(e.target.value)} />
                <label className="check-row select-all-row"><input type="checkbox" checked={filteredLearners.length > 0 && filteredLearners.every((learner) => form.memberIds.includes(learner.id))} onChange={toggleVisibleMembers} /> Select all visible</label>
              </div>
              <div className="checkbox-grid scroll-box">
                {filteredLearners.map((learner) => (
                  <label key={learner.id} className="check-row"><input type="checkbox" checked={form.memberIds.includes(learner.id)} onChange={() => toggleMember(learner.id)} /> {fullName(learner)}</label>
                ))}
              </div>
            </div>
            <button className="button button-primary" type="submit">Create group</button>
            {message && <p className="muted small">{message}</p>}
          </form>
        </Card>
        <Card>
          <h2>Group assignment behavior</h2>
          <p>Assigning a group creates individual learner assignments. Removing someone from a group later should not automatically delete their historical training records.</p>
        </Card>
      </div>
      <Card>
        <DataTable
          rows={rows}
          columns={[
            { key: 'name', label: 'Group' },
            { key: 'description', label: 'Description' },
            { key: 'memberIds', label: 'Members', render: (row) => row.memberIds.length },
            { key: 'members', label: 'Member names' },
            { key: 'status', label: 'Status', render: (row) => <StatusBadge status={row.status} /> }
          ]}
        />
      </Card>
    </>
  );
}
